import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Page, Pageable} from "../../../../utils/pageable";
import {forkJoin, map, Observable, of, Subscription, switchMap, tap} from "rxjs";
import {AppEventsService} from "../../../services/app-events.service";
import {SpinnerService} from "../../../../utils/services/spinner.service";
import {WebSocketService} from "../../../services/web-socket.service";
import {environment} from "../../../../../environments/environment";
import {CourseFilterComponent} from "../../../../utils/course-filter/course-filter.component";
import {CourseFilter, FilterDefinition} from "../../../../model/CourseFilters";
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../../../auth_profile/services/authorization-service.provider";
import * as LessonsV3 from "../../../model/v3/lessons";
import {ColNativeV3Api} from "../../../services/api/v3/col-native-v3-api";
import {LessonBundlesUtilsV3} from "../../../utils/v3/lesson-bundles-utils";

@Component({
    selector: 'app-student-credits-short',
    templateUrl: './student-credits-short.component.html',
    styleUrls: ['./student-credits-short.component.scss'],
    standalone: false
})
export class StudentCreditsShortComponent implements OnInit, OnDestroy {

  bundles: Page<LessonsV3.LessonBundle>;
  readonly pageable: Pageable = LessonBundlesUtilsV3.allBundlesPageable();
  creditsTotal = 0;
  private _studentId: number;
  private schoolId: number;
  isLoading = true;
  wsSubscription: Subscription;
  scheduleSubscription: Subscription;
  authSubscription: Subscription;
  private langs: string[];
  activeCourses: string[] = [];
  activeCoursesBundles = new Map<string, number>();

  @Input() set studentId(id: number) {
    this._studentId = id;
    this.listenToWebSocketCreditsUpdate();
    this.initQuery();
  }

  constructor(
    private appEvents: AppEventsService,
    private spinner: SpinnerService,
    private webSocket: WebSocketService,
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider,
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.auth.getAuthDetailsService().pipe(
      switchMap((api) => api.getSelfSchoolId()),
      tap((schoolId) => {
        this.schoolId = schoolId;
        this.initQuery();
      })
    ).subscribe();
    this.scheduleSubscription = this.appEvents.creditsUpdate.subscribe((_) => {
      this.initQuery();
    });
  }

  ngOnDestroy(): void {
    if(this.authSubscription) this.authSubscription.unsubscribe();
    if (this.scheduleSubscription) {
      this.scheduleSubscription.unsubscribe();
    }
    if(this.wsSubscription) this.wsSubscription.unsubscribe()
  }

  getLangs(): string[] {
    this.langs ??= CourseFilter.filters.map((f) => f.filter).slice(1);
    return this.langs;
  }

  private initQuery() {
    if (!this.schoolId || !this._studentId) {
      return;
    }
    this.activeCourses = [];
    this.activeCoursesBundles.clear();
    this.spinner.trace<Page<LessonsV3.LessonBundle>>(
      this.provider.colV3().pipe(
        switchMap((api) =>
          forkJoin(
            this.getLangs().map((lang) =>
              api
                .getProductContexts(this.schoolId, this._studentId, lang)
                .pipe(map((contexts) => ({lang, hasContext: !!contexts?.length})))
            )
          ).pipe(
            tap((contextsByLang) => {
              this.activeCourses = contextsByLang
                .filter((entry) => entry.hasContext)
                .map((entry) => entry.lang);
            }),
            switchMap(() => this.getAvailableBundles(api))
          )
        )
      )
    ).subscribe((result) => {
        this.bundles = result;
        this.creditsTotal = LessonBundlesUtilsV3.sumAvailableCredits(this.bundles);
        this.appEvents.credits(null).next(this.creditsTotal);
        this.mapBundles();
        this.isLoading = false;
      });
  }


  private mapBundles() {
    const bundlesByCode = LessonBundlesUtilsV3.creditsByProductCode(this.bundles);
    this.activeCourses.forEach((langCode) => {
      this.activeCoursesBundles.set(langCode, bundlesByCode.get(langCode) ?? 0);
    });
  }

  private getAvailableBundles(api: ColNativeV3Api): Observable<Page<LessonsV3.LessonBundle>> {
    if (!this.schoolId || !this._studentId) {
      return of({content: []} as Page<LessonsV3.LessonBundle>);
    }
    return api.getStudentBundles(
      this.schoolId,
      this._studentId,
      this.pageable,
      null,
      true
    );
  }

  listenToWebSocketCreditsUpdate(): void {
    if (!this._studentId) {return; }
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = this.webSocket.establish(environment.eventsWsEndpoint)
      .pipe(
        switchMap(connection => connection.subscribe(
            `/col/student/${this.studentId}/creditsUpdate`
          )
        )
      ).subscribe(() =>
        this.initQuery()
      )
  }

  getFilterDefinitionByLang(langCode: string): FilterDefinition | undefined {
    return CourseFilterComponent.getFiltersShort().find((filter) => filter.filter === langCode);
  }

  getLanguageTranslation(langCode: string) {
    const filter = this.getFilterDefinitionByLang(langCode);
    if (!filter) return '';
    return "EBOOKS.FILTER_LANGS." + filter.translationCode;
  }
}
